import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import {
  camelCase,
  mapKeys,
} from 'lodash';

import {
  Box,
  Container,
  Link,
} from '@visibly/vui';

import QualificationForm from '../../shared/components/QualificationForm';
import OnboardingIneligibilityResult from '../../examResults/components/OnboardingIneligibilityResult';
import AccountForm from './signup/AccountForm';

const Step = {
  Qualification: 0,
  Account: 1,
};

function Signup(props) {
  const {
    partner,
    componentProps,
  } = props;

  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(Step.Qualification);
  const [user, setUser] = useState(props.user);
  const [disqualification, setDisqualification] = useState(null);

  const [
    qualificationFormSubmitIsLoading,
    setQualificationFormSubmitIsLoading,
  ] = useState(false);

  const qualificationFormSubmitHandler = async (qualificationFormUser) => {
    const updatedUser = { ...user, ...qualificationFormUser };
    setUser(updatedUser);

    const url = new URL(partner.urls.checkEligibility);

    const searchParams = new URLSearchParams(url.search);
    searchParams.append('user[region_id]', updatedUser.regionId);
    searchParams.append('user[birthday]', updatedUser.dateOfBirth);

    url.search = new URLSearchParams(searchParams).toString();

    const handleError = () => {
      setQualificationFormSubmitIsLoading(false);

      // eslint-disable-next-line no-alert
      alert(t('signup.errorMessage'));
    };

    let resp;
    setQualificationFormSubmitIsLoading(true);

    try {
      resp = await fetch(url.toString(), { method: 'get' });
    } catch (error) {
      handleError();
      throw error;
    }

    if (!resp.ok) {
      handleError();
      return;
    }

    const respBody = await resp.json();
    const data = mapKeys(respBody, (_, k) => camelCase(k));

    if (data.qualifies) {
      setCurrentStep(Step.Account);
    } else {
      setDisqualification(data.disqualifications.pop());
    }
  };

  return (
    <Container size='sm'>
      <Box margin='$xl'>
        <a
          href={partner.urls.root}
          className="prelogin__login__logo"
        >
          <img src={partner.urls.logo} alt={partner.name} />
        </a>

        {currentStep === Step.Qualification &&
          <>
            {!disqualification &&
              <QualificationForm
                {...componentProps.qualificationForm}
                partner={partner}
                user={user}
                onSubmitIsLoading={qualificationFormSubmitIsLoading}
                onSubmit={qualificationFormSubmitHandler}
              />
            }

            {disqualification &&
              <OnboardingIneligibilityResult
                isDtc={user.isDtc}
                reason={disqualification.code}
                ageRange={disqualification.age_range}
              />
            }
          </>
        }

        {currentStep === Step.Account &&
          <AccountForm
            {...componentProps.accountForm}
            partner={partner}
            user={user}
          />
        }

        {!disqualification &&
          <Box textAlign='center' marginTop='$xxl'>
            <Trans i18nKey="signup.logIn">
              0
              <Link href={partner.urls.login} className="viz-link branded__link">1</Link>
            </Trans>
          </Box>
        }
      </Box>
    </Container>
  );
}

Signup.propTypes = {
  partner: PropTypes.shape({
    name: PropTypes.string.isRequired,
    urls: PropTypes.shape({
      root: PropTypes.string.isRequired,
      login: PropTypes.string.isRequired,
      signup: PropTypes.string.isRequired,
      checkEligibility: PropTypes.string.isRequired,
      termsOfUse: PropTypes.string.isRequired,
      privacyPractices: PropTypes.string.isRequired,
      privacyPolicy: PropTypes.string.isRequired,
      serviceAgreement: PropTypes.string.isRequired,
      logo: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,

  user: PropTypes.shape({
    isDtc: PropTypes.bool.isRequired,
  }).isRequired,

  componentProps: PropTypes.exact({
    qualificationForm: PropTypes.object.isRequired,
    accountForm: PropTypes.object.isRequired,
  }).isRequired,
};

export default Signup;
